import { CarPolicyTypes, PAGES } from 'common/enums';
import { useCarDealershipSettingGetListQuery } from 'hooks/react-query/car-dealership-setting/get-list/get-list.query';
import { useCompaniesByTypesQuery } from 'hooks/react-query/company/companies-by-types/companies-by-types.query';
import { useGetCarDealershipsQuery } from 'hooks/react-query/company/get-car-dealerships/get-car-dealerships.query';
import { useCrmEntityGetEntityByFieldQuery } from 'hooks/react-query/crm-entity/get-entity-by-field/get-entity-by-field.query';
import { useGetDamageRecordingFormDataQuery } from 'hooks/react-query/damage/get-damage-recording-form-data/get-damage-recording-form-data.query';
import { useDocumentTypeByAppQuery } from 'hooks/react-query/document-type/document-type-by-app/document-type-by-app';
import { useGetDriversWithCarQuery } from 'hooks/react-query/driver/get-drivers-with-car/get-drivers-with-car.query';
import { useGetVehicleListVRQuery } from 'hooks/react-query/fleet-lease-end/get-vehicle-list-vr/get-vehicle-list-vr.query';
import { useDescriptionChoicesQuery } from 'hooks/react-query/fleet/description-choices/description-choices.query';
import { useUnusedFleetQuery } from 'hooks/react-query/fleet/unused-fleet/unused-fleet.query';
import { usePolicyCompanyListQuery } from 'hooks/react-query/policy/company-list/company-list.query';
import { useTypedSelector } from 'hooks/use-typed-selector';
import { FormSourceEnum } from '../use-set-options.props';
import { sourceFetchPayload } from './source-fetch-payload';
import { IS_TEST_COMPANY } from '@/constants';
import { useMemo } from 'react';
import useGetPersonsForLocations from '../../../../../../hooks/react-query/use-get-persons-for-locations';
import { useFuelReportNameFilterQuery } from 'hooks/react-query/fuel-reports/fuel-reports-filter';
import { useDriversForBookingQuery } from 'hooks/react-query/pool-calendar/use-drivers-for-booking';
import { useDriverLevelListQuery } from 'hooks/react-query/driver-level/company-list/driver-level.query';
import { LookupResponse } from 'common/interfaces';
import useGetLookupByColumnNameQuery from 'hooks/react-query/incoming-invoice/get-lookup-by-column-name';
import { IncomingInvoiceFieldNames, SplitBookingFieldNames } from 'entities/incoming-invoice/model/types';
import {
  GetLookupByColumnNameQueryResponseItem,
  LedgerAccountAttributes,
  TaxCodeAttributes,
  VehicleAttributes
} from 'hooks/react-query/incoming-invoice/get-lookup-by-column-name/get-lookup-by-column-name.types';
import { useAllActiveComapany } from 'hooks/react-query/company/get-all-active-company/get-all-active-company.query';

export const useFormSourceMapping = (search: string, source?: FormSourceEnum) => {
  const user = useTypedSelector(state => state.app.user);
  const { getDriversWithCarQuery } = useGetDriversWithCarQuery(
    { sparteId: user.companyId, activeOnly: true },
    { options: { enabled: source === FormSourceEnum.GetCompanyDrivers } }
  );
  const { companiesByTypesQuery: getContractPartnerCompaniesQuery } = useCompaniesByTypesQuery(
    sourceFetchPayload.getContractPartnerCompanies,
    { options: { enabled: source === FormSourceEnum.GetContractPartnerCompanies } }
  );
  const { allActiveComapany } = useAllActiveComapany(undefined, source === FormSourceEnum.GetUserCompanies);
  const { crmEntityGetEntityByFieldQuery: getCarBrandsQuery } = useCrmEntityGetEntityByFieldQuery(
    sourceFetchPayload.getCarBrands,
    { options: { enabled: source === FormSourceEnum.GetCarBrands } }
  );
  const { crmEntityGetEntityByFieldQuery: getCarBrandsForGrv } = useCrmEntityGetEntityByFieldQuery(
    sourceFetchPayload.getCarBrandsForGrv,
    { options: { enabled: source === FormSourceEnum.GetCarBrandsForGrv } }
  );
  const { crmEntityGetEntityByFieldQuery: getCompanyContractsQuery } = useCrmEntityGetEntityByFieldQuery(
    sourceFetchPayload.getCompanyContracts(user.companyId),
    { options: { enabled: source === FormSourceEnum.GetCompanyContracts } }
  );
  const { unusedFleetQuery } = useUnusedFleetQuery(undefined, {
    options: { enabled: source === FormSourceEnum.GetUnusedUserCompanyFuhrparks }
  });
  const { getCarDealershipsQuery } = useGetCarDealershipsQuery(['accountid', 'name'], {
    options: { enabled: source === FormSourceEnum.GetCarDealerships }
  });
  const { policyCompanyListQuery } = usePolicyCompanyListQuery(
    {},
    source === FormSourceEnum.GetUserCompanyCarPolicies || source === FormSourceEnum.GetUserCompanyPoolcarCarPolicies
  );
  const { driverLevelListQuery } = useDriverLevelListQuery({}, source === FormSourceEnum.GetUserCompanyDriverLevels);
  const { getVehicleListVRQuery } = useGetVehicleListVRQuery({
    options: { enabled: source === FormSourceEnum.GetCarsListVR }
  });
  const { companiesByTypesQuery } = useCompaniesByTypesQuery(sourceFetchPayload.getServices, {
    options: { enabled: source === FormSourceEnum.GetServices }
  });
  const { carDealershipSettingGetListQuery } = useCarDealershipSettingGetListQuery('', {
    options: { enabled: source === FormSourceEnum.GetUserCompanyCarDealerships }
  });
  const { descriptionChoicesQuery } = useDescriptionChoicesQuery({
    options: { enabled: source === FormSourceEnum.GetDocumentTypes }
  });
  const { query: damageRecordMarkQuery } = useGetDamageRecordingFormDataQuery(
    { search, formDataKey: 'kennzeichen' },
    { options: { enabled: source === FormSourceEnum.GetDamageRecordMark } }
  );
  const { query: damageRecordRepairPartnerQuery } = useGetDamageRecordingFormDataQuery(
    { search, formDataKey: 'reparaturpartner' },
    { options: { enabled: source === FormSourceEnum.GetDamageRecordRepairPartner } }
  );
  const { query: damageRecordWorkshopQuery } = useGetDamageRecordingFormDataQuery(
    { search, formDataKey: 'werkstatt' },
    { options: { enabled: source === FormSourceEnum.GetDamageRecordWorkshop } }
  );
  const { query: damageRecordOwnerQuery } = useGetDamageRecordingFormDataQuery(
    { search, formDataKey: 'eigentumer' },
    { options: { enabled: source === FormSourceEnum.GetDamageRecordOwner } }
  );
  const { query: damageRecordInsurerQuery } = useGetDamageRecordingFormDataQuery(
    { search, formDataKey: 'versicherer' },
    { options: { enabled: source === FormSourceEnum.GetDamageRecordInsurer } }
  );
  const { query: damageRecordInsuranceBrokerQuery } = useGetDamageRecordingFormDataQuery(
    { search, formDataKey: 'versicherungsmarkler' },
    { options: { enabled: source === FormSourceEnum.GetDamageRecordInsuranceBroker } }
  );
  const { documentTypeByAppQuery } = useDocumentTypeByAppQuery(PAGES.DAMAGE, {
    options: { enabled: source === FormSourceEnum.GetDamagePageDocumentTypes }
  });
  const { personsForLocations } = useGetPersonsForLocations({
    enabled: source === FormSourceEnum.GetPersonsForLocations
  });
  const { driversForBooking } = useDriversForBookingQuery({
    options: { enabled: source === FormSourceEnum.GetDriversForBooking }
  });

  const { getFuelReportFilterQuery: getFuelReportFilterNameQuery } = useFuelReportNameFilterQuery(
    { searchValue: search, columnName: 'new_name' },
    { options: { enabled: source === FormSourceEnum.GetFuelReportsFilterName } }
  );

  const { getFuelReportFilterQuery: getFuelReportFilterDriverQuery } = useFuelReportNameFilterQuery(
    { searchValue: search, columnName: 'new_fahrerid' },
    { options: { enabled: source === FormSourceEnum.GetFuelReportsFilterDriver } }
  );

  const { getFuelReportFilterQuery: getFuelReportFilterLocationQuery } = useFuelReportNameFilterQuery(
    { searchValue: search, columnName: 'new_standortid' },
    { options: { enabled: source === FormSourceEnum.GetFuelReportsFilterLocation } }
  );

  const { getFuelReportFilterQuery: getFuelReportFilterBranchQuery } = useFuelReportNameFilterQuery(
    { searchValue: search, columnName: 'new_sparteid' },
    { options: { enabled: source === FormSourceEnum.GetFuelReportsFilterBranch } }
  );

  const { getFuelReportFilterQuery: getFuelReportFilterManufacturerQuery } = useFuelReportNameFilterQuery(
    { searchValue: search, columnName: 'new_hersteller' },
    { options: { enabled: source === FormSourceEnum.GetFuelReportsFilterManufacturer } }
  );

  const { getFuelReportFilterQuery: getFuelReportFilterModelQuery } = useFuelReportNameFilterQuery(
    { searchValue: search, columnName: 'new_model' },
    { options: { enabled: source === FormSourceEnum.GetFuelReportsFilterModel } }
  );

  const { getFuelReportFilterQuery: getFuelReportFilterCostCenterId } = useFuelReportNameFilterQuery(
    { searchValue: search, columnName: 'new_kostenstelleid' },
    { options: { enabled: source === FormSourceEnum.GetFuelReportsFilterCostCenterId } }
  );

  const { getFuelReportFilterQuery: getFuelReportFilterCostCenter2 } = useFuelReportNameFilterQuery(
    { searchValue: search, columnName: 'uds_kostenstelle2' },
    { options: { enabled: source === FormSourceEnum.GetFuelReportsFilterCostCenter2 } }
  );

  const { getFuelReportFilterQuery: getFuelReportFilterCostCenter3 } = useFuelReportNameFilterQuery(
    { searchValue: search, columnName: 'uds_kostenstelle3' },
    { options: { enabled: source === FormSourceEnum.GetFuelReportsFilterCostCenter3 } }
  );

  const { query: splitBookingOptionsVehicle } = useGetLookupByColumnNameQuery<
    GetLookupByColumnNameQueryResponseItem<VehicleAttributes>
  >(
    { searchValue: search, columnName: SplitBookingFieldNames.Vehicle },
    { enabled: source === FormSourceEnum.GetSplitBookingOptionsVehicle }
  );
  const { query: splitBookingOptionsLedgerAccount } = useGetLookupByColumnNameQuery<LedgerAccountAttributes>(
    { searchValue: search, columnName: SplitBookingFieldNames.LedgerAccount },
    { enabled: source === FormSourceEnum.GetSplitBookingOptionsLedgerAccount }
  );
  const { query: splitBookingOptionsTaxCode } = useGetLookupByColumnNameQuery<
    GetLookupByColumnNameQueryResponseItem<TaxCodeAttributes>
  >(
    { searchValue: search, columnName: SplitBookingFieldNames.TaxCode },
    { enabled: source === FormSourceEnum.GetSplitBookingOptionsTaxCode }
  );
  const { query: incomingInvoiceOptionsFrameworkAgreement } = useGetLookupByColumnNameQuery<LookupResponse>(
    { searchValue: search, columnName: IncomingInvoiceFieldNames.FrameworkAgreement },
    { enabled: source === FormSourceEnum.GetIncomingInvoiceOptionsFrameworkAgreement }
  );
  const { query: incomingInvoiceOptionsIncidentNumber } = useGetLookupByColumnNameQuery<LookupResponse>(
    { searchValue: search, columnName: IncomingInvoiceFieldNames.IncidentNumber },
    { enabled: source === FormSourceEnum.GetIncomingInvoiceOptionsIncidentNumber }
  );

  const sourceMapping = useMemo((): { value: string; label: string }[] => {
    switch (source) {
      case FormSourceEnum.GetCarBrands:
        return getCarBrandsQuery.entityes.map(item => ({
          value: item.uds_carbrandid,
          label: item.uds_name
        }));
      case FormSourceEnum.GetCarBrandsForGrv:
        return getCarBrandsForGrv.entityes.map(item => ({
          value: item.uds_carbrandid,
          label: item.uds_name
        }));
      case FormSourceEnum.GetCompanyContracts:
        return getCompanyContractsQuery.entityes.map(item => ({
          value: item.uds_rahmenvertrgesid,
          label: item.uds_vertragsbezeichnung || '-Leere Vertragsbezeichnung-'
        }));
      case FormSourceEnum.GetCompanyDrivers:
        return getDriversWithCarQuery.map(item => ({ label: item.name, value: item.id }));
      case FormSourceEnum.GetContractPartnerCompanies:
        return getContractPartnerCompaniesQuery
          .filter(item => item.uds_testcompanycode !== IS_TEST_COMPANY.yes.value || user.isTestUser)
          .sort((a, b) => a.name?.localeCompare(b.name))
          .map(item => ({ value: item.accountid, label: item.name }));
      case FormSourceEnum.GetUserCompanies: {
        const companies = allActiveComapany
          ? [allActiveComapany.currentAccount, ...allActiveComapany.childAccounts]
          : [{ id: user.companyId, name: user.companyName }, ...(user.childCompanies || [])];

        return companies
          .filter(company => company.id && company.name)
          .map(company => ({ value: company.id, label: company.name }));
      }
      case FormSourceEnum.GetUnusedUserCompanyFuhrparks:
        return unusedFleetQuery.map(({ attributes: item }) => ({
          value: item.new_fuhrparkid || '-',
          label: `${item.new_name}${
            item.new_hersteller || item.new_model ? ` (${item.new_hersteller || ''} ${item.new_model || ''})` : ''
          }`
        }));
      case FormSourceEnum.GetCarDealerships:
        return getCarDealershipsQuery.map(item => ({
          value: item.accountid,
          label: item.name
        }));
      case FormSourceEnum.GetUserCompanyCarPolicies:
        return policyCompanyListQuery.items.map(item => ({
          value: item.uds_carpolicyid || '-',
          label: item.uds_name || '-'
        }));
      case FormSourceEnum.GetUserCompanyPoolcarCarPolicies:
        return policyCompanyListQuery.items
          .filter(item => String(item.uds_type) === CarPolicyTypes.PoolCar)
          .map(item => ({
            value: item.uds_carpolicyid || '-',
            label: item.uds_name || '-'
          }));
      case FormSourceEnum.GetUserCompanyDriverLevels:
        return driverLevelListQuery.items.map(item => ({
          value: item.uds_car_policy_nutzerid || '-',
          label: item.uds_name || '-'
        }));
      case FormSourceEnum.GetCarsListVR:
        return getVehicleListVRQuery.fuhrparkList.map(item => ({
          value: item.id || '-',
          label: item.name || '-',
          showEndAdornment: item.lafPresent
        }));
      case FormSourceEnum.GetServices:
        return companiesByTypesQuery.map(item => ({
          value: item.accountid || '-',
          label: item.name || '-'
        }));
      case FormSourceEnum.GetDamageRecordMark:
        return damageRecordMarkQuery.map(item => ({
          value: item.id,
          label: item.name
        }));
      case FormSourceEnum.GetDamageRecordRepairPartner:
        return damageRecordRepairPartnerQuery.map(item => ({
          value: item.id,
          label: item.name
        }));
      case FormSourceEnum.GetDamageRecordWorkshop:
        return damageRecordWorkshopQuery.map(item => ({
          value: item.id,
          label: item.name
        }));
      case FormSourceEnum.GetDamageRecordOwner:
        return damageRecordOwnerQuery.map(item => ({
          value: item.id,
          label: item.name
        }));
      case FormSourceEnum.GetDamageRecordInsurer:
        return damageRecordInsurerQuery.map(item => ({
          value: item.id,
          label: item.name
        }));
      case FormSourceEnum.GetDamageRecordInsuranceBroker:
        return damageRecordInsuranceBrokerQuery.map(item => ({
          value: item.id,
          label: item.name
        }));
      case FormSourceEnum.GetDamagePageDocumentTypes:
        return documentTypeByAppQuery.documentTypes.map(item => ({
          value: item.id,
          label: item.name
        }));
      case FormSourceEnum.GetDocumentTypes:
        return descriptionChoicesQuery
          .sort((a: LookupResponse, b: LookupResponse) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
          .map(item => ({ value: item.id, label: item.name }));
      case FormSourceEnum.GetUserCompanyCarDealerships:
        return (
          carDealershipSettingGetListQuery?.map(item => ({
            value: item.uds_autohandlerid_id || '-',
            label: item.uds_autohandlerid || '-'
          })) || []
        );
      case FormSourceEnum.GetPersonsForLocations:
        return (
          personsForLocations?.map(item => ({
            value: item.contactid,
            label: item.fullname || '-'
          })) || []
        );

      case FormSourceEnum.GetFuelReportsFilterName:
        return (
          getFuelReportFilterNameQuery.new_name?.map(item => ({
            value: item.id,
            label: item.name,
            active: item?.active ?? null
          })) || []
        );

      case FormSourceEnum.GetFuelReportsFilterDriver:
        return (
          getFuelReportFilterDriverQuery.new_fahrerid?.map(item => ({
            value: item.id,
            label: item.name
          })) || []
        );

      case FormSourceEnum.GetFuelReportsFilterLocation:
        return (
          getFuelReportFilterLocationQuery.new_standortid?.map(item => ({
            value: item.id,
            label: item.name
          })) || []
        );

      case FormSourceEnum.GetFuelReportsFilterBranch:
        return (
          getFuelReportFilterBranchQuery.new_sparteid?.map(item => ({
            value: item.id,
            label: item.name
          })) || []
        );

      case FormSourceEnum.GetFuelReportsFilterManufacturer:
        return (
          getFuelReportFilterManufacturerQuery.new_hersteller?.map(item => ({
            value: item,
            label: item
          })) || []
        );

      case FormSourceEnum.GetFuelReportsFilterModel:
        return (
          getFuelReportFilterModelQuery.new_model?.map(item => ({
            value: item,
            label: item
          })) || []
        );

      case FormSourceEnum.GetFuelReportsFilterCostCenterId:
        return (
          getFuelReportFilterCostCenterId.new_kostenstelleid?.map(item => ({
            value: item.id,
            label: item.name
          })) || []
        );

      case FormSourceEnum.GetFuelReportsFilterCostCenter2:
        return (
          getFuelReportFilterCostCenter2.uds_kostenstelle2?.map(item => ({
            value: item,
            label: item
          })) || []
        );

      case FormSourceEnum.GetFuelReportsFilterCostCenter3:
        return (
          getFuelReportFilterCostCenter3.uds_kostenstelle3?.map(item => ({
            value: item,
            label: item
          })) || []
        );
      case FormSourceEnum.GetDriversForBooking:
        return driversForBooking.map(item => ({ label: item.name, value: item.id }));
      case FormSourceEnum.GetSplitBookingOptionsVehicle:
        return splitBookingOptionsVehicle.map(item => ({
          value: item.id,
          label: item.name,
          active: !item.attributes.statecode.value,
          description: `${item.attributes.new_fin ? `${item.attributes.new_fin}\n` : ''}${item.attributes.new_status_carcode?.label || ''}`
        }));
      case FormSourceEnum.GetSplitBookingOptionsLedgerAccount:
        return splitBookingOptionsLedgerAccount.map(item => ({
          value: item.new_ledgeraccountid,
          label: item.name || '',
          description: item.new_laname || ''
        }));
      case FormSourceEnum.GetSplitBookingOptionsTaxCode:
        return splitBookingOptionsTaxCode.map(item => ({
          value: item.attributes.msdyn_taxcodeid || '',
          label: item.attributes.msdyn_name || ''
        }));
      case FormSourceEnum.GetIncomingInvoiceOptionsFrameworkAgreement:
        return incomingInvoiceOptionsFrameworkAgreement.map(item => ({
          value: item.id,
          label: item.name
        }));
      case FormSourceEnum.GetIncomingInvoiceOptionsIncidentNumber:
        return incomingInvoiceOptionsIncidentNumber.map(item => ({
          value: item.id,
          label: item.name
        }));

      default:
        return [];
    }
  }, [
    JSON.stringify(descriptionChoicesQuery),
    JSON.stringify(companiesByTypesQuery),
    JSON.stringify(damageRecordInsuranceBrokerQuery),
    JSON.stringify(damageRecordInsurerQuery),
    JSON.stringify(damageRecordMarkQuery),
    JSON.stringify(damageRecordOwnerQuery),
    JSON.stringify(damageRecordRepairPartnerQuery),
    JSON.stringify(damageRecordWorkshopQuery),
    JSON.stringify(documentTypeByAppQuery.documentTypes),
    JSON.stringify(getCarBrandsForGrv.entityes),
    JSON.stringify(allActiveComapany),
    JSON.stringify(getCarBrandsQuery.entityes),
    JSON.stringify(getCarDealershipsQuery),
    JSON.stringify(getCompanyContractsQuery.entityes),
    JSON.stringify(getContractPartnerCompaniesQuery),
    JSON.stringify(getDriversWithCarQuery),
    JSON.stringify(getVehicleListVRQuery.fuhrparkList),
    JSON.stringify(policyCompanyListQuery.items),
    JSON.stringify(unusedFleetQuery),
    JSON.stringify(carDealershipSettingGetListQuery),
    JSON.stringify(personsForLocations),
    JSON.stringify(getFuelReportFilterNameQuery),
    JSON.stringify(getFuelReportFilterDriverQuery),
    JSON.stringify(getFuelReportFilterLocationQuery),
    JSON.stringify(getFuelReportFilterBranchQuery),
    JSON.stringify(getFuelReportFilterManufacturerQuery),
    JSON.stringify(getFuelReportFilterModelQuery),
    JSON.stringify(getFuelReportFilterCostCenterId),
    JSON.stringify(getFuelReportFilterCostCenter2),
    JSON.stringify(getFuelReportFilterCostCenter3),
    JSON.stringify(driversForBooking),
    JSON.stringify(splitBookingOptionsVehicle),
    JSON.stringify(splitBookingOptionsLedgerAccount),
    JSON.stringify(splitBookingOptionsTaxCode),
    JSON.stringify(incomingInvoiceOptionsFrameworkAgreement),
    JSON.stringify(incomingInvoiceOptionsIncidentNumber),
    source,
    user.isTestUser
  ]);

  return { sourceMapping };
};
